import {
  useState,
  useEffect,
  useRef,
  useId,
  isValidElement,
  Children,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
  type ReactElement,
} from 'react';
import { ChevronDown, Check } from 'lucide-react';
import styles from './Input.module.css';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export function Input({ label, error, className = '', ...props }: InputProps) {
  return (
    <div className={styles.field}>
      {label && <label className={styles.label}>{label}</label>}
      <input
        className={`${styles.input} ${error ? styles.inputError : ''} ${className}`}
        {...props}
      />
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export function Textarea({ label, error, className = '', ...props }: TextareaProps) {
  return (
    <div className={styles.field}>
      {label && <label className={styles.label}>{label}</label>}
      <textarea
        className={`${styles.input} ${styles.textarea} ${error ? styles.inputError : ''} ${className}`}
        {...props}
      />
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  error?: string;
  children: ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function Select({ label, error, className = '', children, value, onChange, disabled }: SelectProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Parse <option> children into {value, label} pairs.
  const options = Children.toArray(children)
    .filter((child): child is ReactElement => isValidElement(child) && (child as ReactElement).type === 'option')
    .map((child) => {
      const p = (child as ReactElement<{ value?: string; children?: ReactNode }>).props;
      return {
        value: String(p.value ?? ''),
        label: String(p.children ?? p.value ?? ''),
      };
    });

  const selectedLabel = options.find((o) => o.value === String(value ?? ''))?.label ?? String(value ?? '');

  // Reset highlight when opening
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === String(value ?? ''));
      setHighlightIndex(idx >= 0 ? idx : 0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleSelect(optValue: string) {
    onChange?.({ target: { value: optValue } } as React.ChangeEvent<HTMLSelectElement>);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          setHighlightIndex((prev) => Math.min(prev + 1, options.length - 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
        }
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setHighlightIndex(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setHighlightIndex(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else if (highlightIndex >= 0 && highlightIndex < options.length) {
          handleSelect(options[highlightIndex].value);
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        break;
    }
  }

  const activeDescendant =
    open && highlightIndex >= 0 ? `${listboxId}-option-${highlightIndex}` : undefined;

  return (
    <div className={styles.field}>
      {label && <label className={styles.label}>{label}</label>}
      <div ref={wrapRef} className={styles.selectWrap}>
        <button
          type="button"
          className={`${styles.selectTrigger} ${open ? styles.selectTriggerOpen : ''} ${error ? styles.inputError : ''} ${className}`}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
        >
          <span>{selectedLabel}</span>
          <ChevronDown
            size={12}
            strokeWidth={2}
            className={`${styles.selectChevron} ${open ? styles.selectChevronOpen : ''}`}
          />
        </button>

        {open && (
          <div className={styles.selectPanel} role="listbox" id={listboxId}>
            {options.map((opt, idx) => (
              <button
                key={opt.value}
                id={`${listboxId}-option-${idx}`}
                type="button"
                role="option"
                aria-selected={opt.value === String(value ?? '')}
                className={`${styles.selectOption} ${opt.value === String(value ?? '') ? styles.selectOptionActive : ''} ${idx === highlightIndex ? styles.selectOptionHighlight : ''}`}
                onClick={() => handleSelect(opt.value)}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                <span className={styles.selectOptionCheck}>
                  {opt.value === String(value ?? '') && <Check size={11} strokeWidth={2.5} />}
                </span>
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
}
